/**
 * Theme Provider
 * 
 * Applies theme colors from database settings to CSS variables.
 * Loads theme on app initialization.
 */

import { useEffect } from "react";
import { useSiteSettings } from "@/hooks/useSiteSettings";

type ThemeColors = {
  heritage?: string;
  culture?: string;
  accent?: string;
  primary?: string;
};

type ThemeData = {
  preset?: string;
  customColors?: ThemeColors;
};

/**
 * Apply theme colors to CSS custom properties
 */
export const applyThemeColors = (colors: ThemeColors) => {
  const root = document.documentElement;
  
  Object.entries(colors).forEach(([key, value]) => {
    if (value) {
      root.style.setProperty(`--${key}`, value);
    }
  });
};

/**
 * Reset theme to default CSS values
 */
export const resetTheme = () => {
  const root = document.documentElement;
  const cssVars = ["--heritage", "--culture", "--accent", "--primary"];
  
  cssVars.forEach(varName => {
    root.style.removeProperty(varName);
  });
};

/**
 * ThemeProvider component
 * 
 * Wraps the app and applies theme from database on mount.
 * Does not render any DOM elements, just applies CSS variables.
 */
export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
  const { data, isLoading } = useSiteSettings();

  useEffect(() => {
    if (isLoading || !data?.features) return;

    const theme = data.features.theme as ThemeData | undefined;
    
    if (theme?.customColors) {
      applyThemeColors(theme.customColors);
    }
  }, [data?.features, isLoading]);

  return <>{children}</>;
};

export default ThemeProvider;





